import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";

import type { IUserKeywordGroup, IInitialKeywordGroup } from "../keywordGroups.types";

import { useCreateKeywordGroup } from "./useCreateKeywordGroup";
import { useUpdateKeywordGroup } from "./useUpdateKeywordGroup";

type CreateOrUpdateInput = (IInitialKeywordGroup & { Id?: string }) | IUserKeywordGroup;

const isNewGroup = (group: CreateOrUpdateInput): group is IInitialKeywordGroup => {
    return !group.Id;
};

/**
 * @deprecated Use useCreateKeywordGroupApiClient or useUpdateKeywordGroupApiClient from pro-api-client instead.
 * This hook will be removed in a future version.
 * New implementations should use the separate create/update hooks from the API client for better type safety.
 */
export const useCreateOrUpdateKeywordGroup = (
    options?: Omit<UseMutationOptions<IUserKeywordGroup | undefined | null, Error, CreateOrUpdateInput>, "mutationFn">,
) => {
    const createMutation = useCreateKeywordGroup();
    const updateMutation = useUpdateKeywordGroup();

    return useMutation({
        mutationFn: async (group: CreateOrUpdateInput): Promise<IUserKeywordGroup | undefined | null> => {
            if (isNewGroup(group)) {
                return await createMutation.mutateAsync(group);
            } else {
                return await updateMutation.mutateAsync(group);
            }
        },
        onSuccess: (result, variables, context) => {
            options?.onSuccess?.(result, variables, context);
        },
        onError: (error, variables, context) => {
            options?.onError?.(error, variables, context);
        },
        ...options,
    });
};
